import { Button, EmptyState, Spinner } from '@freightapi/ui'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import { RecoveryCodeList } from '../components/RecoveryCodeList'
import { TwoFactorSetup } from '../components/TwoFactorSetup'
import {
  asApiError,
  disableTwoFactor,
  fetchTwoFactor,
  regenerateRecoveryCodes,
  setTwoFactorPolicy,
  type TwoFactorStatus,
} from '../lib/api'
import { useConsoleI18n } from '../lib/locale'
import { useCan } from '../lib/session'

/**
 * Security settings (M4-12): the authenticator of the person looking at the screen, and — for
 * whoever may change the organisation — the switch that requires one of every member.
 *
 * IR (EU) 2025/2243 makes a second factor mandatory for the business users of an eFTI platform, so
 * this page is not a preference: it is where an organisation shows that it meets that requirement.
 * The eID half of the same article (eIDAS, assurance level *advanced*) is a separate job and is
 * deliberately not pretended at here.
 */
export const Route = createFileRoute('/_app/settings/security')({ component: Security })

type State =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly two_factor: TwoFactorStatus }
  | { readonly status: 'error'; readonly message: string }

function Security() {
  const { t } = useConsoleI18n()
  const can = useCan()
  const [state, setState] = useState<State>({ status: 'loading' })
  const [enrolling, setEnrolling] = useState(false)
  const [freshCodes, setFreshCodes] = useState<readonly string[] | undefined>(undefined)
  const [error, setError] = useState<string | undefined>(undefined)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      setState({ status: 'ready', two_factor: await fetchTwoFactor() })
    } catch (cause) {
      setState({ status: 'error', message: t(asApiError(cause).messageKey) })
    }
  }, [t])

  useEffect(() => {
    void load()
  }, [load])

  /** Every mutation on this page ends the same way: say what went wrong, then re-read the truth. */
  const run = useCallback(
    async (action: () => Promise<void>) => {
      setBusy(true)
      setError(undefined)
      try {
        await action()
      } catch (cause) {
        const failure = asApiError(cause)
        setError(failure.problem?.detail ?? t(failure.messageKey))
      } finally {
        setBusy(false)
        await load()
      }
    },
    [load, t],
  )

  if (state.status === 'loading') {
    return (
      <div className="flex justify-center py-10">
        <Spinner label={t('loading')} size="lg" />
      </div>
    )
  }
  if (state.status === 'error') {
    return <EmptyState title={t('error.title')} description={state.message} />
  }

  const { enrolled, required, recovery_codes_remaining: remaining } = state.two_factor

  return (
    <section className="flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <h2 className="font-semibold text-lead text-ink">{t('security.title')}</h2>
        <p className="text-body text-muted">{t('security.intro')}</p>
      </div>

      {error === undefined ? null : (
        <p role="alert" className="text-body text-danger">
          {error}
        </p>
      )}

      {freshCodes === undefined ? null : (
        <RecoveryCodeList
          codes={freshCodes}
          titleKey="two_factor.recovery.title"
          introKey="two_factor.recovery.intro"
          onAcknowledge={() => setFreshCodes(undefined)}
        />
      )}

      {enrolled ? (
        <div className="flex flex-col gap-4">
          <p role="status" className="text-body text-ink">
            {t('security.enabled')}
          </p>
          <p className="text-body text-muted">
            {t('security.recovery_remaining', { count: String(remaining) })}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="ghost"
              busy={busy}
              busyLabel={t('loading')}
              onClick={() =>
                void run(async () => {
                  const { recovery_codes } = await regenerateRecoveryCodes()
                  setFreshCodes(recovery_codes)
                })
              }
            >
              {t('security.regenerate')}
            </Button>
            {/*
              Turning it off is refused by the API while the organisation requires one, so the
              button is not offered either: a control that only ever produces a 403 is a lie about
              what the account may do.
            */}
            {required ? null : (
              <Button
                variant="ghost"
                busy={busy}
                busyLabel={t('loading')}
                onClick={() => void run(disableTwoFactor)}
              >
                {t('security.disable')}
              </Button>
            )}
          </div>
          {required ? <p className="text-caption text-muted">{t('security.locked')}</p> : null}
        </div>
      ) : enrolling ? (
        <TwoFactorSetup
          onCompleted={() => {
            setEnrolling(false)
            void load()
          }}
          onCancel={() => setEnrolling(false)}
        />
      ) : (
        <div className="flex flex-col items-start gap-4">
          <p className="text-body text-muted">{t('security.disabled')}</p>
          <Button onClick={() => setEnrolling(true)}>{t('security.enable')}</Button>
        </div>
      )}

      {can('organization:update') ? (
        <div className="flex flex-col gap-3 border-line border-t pt-6">
          <h3 className="font-semibold text-body text-ink">{t('security.policy.title')}</h3>
          <p className="text-body text-muted">{t('security.policy.intro')}</p>
          <label className="flex items-start gap-2 text-body text-ink">
            <input
              type="checkbox"
              className="mt-1"
              checked={required}
              disabled={busy}
              onChange={(event) => {
                const next = event.target.checked
                void run(async () => {
                  await setTwoFactorPolicy(next)
                })
              }}
            />
            <span>{t('security.policy.label')}</span>
          </label>
          <p className="text-caption text-muted">{t('security.policy.hint')}</p>
        </div>
      ) : null}
    </section>
  )
}
